import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import {
  fetchCurrentUser,
  logout as apiLogout,
  platformAdminLogin as apiPlatformAdminLogin,
  type AuthUser,
  type LoginResponse,
} from '@/api/auth';
import { LOGOUT_SIGNAL_KEY, setUnauthorizedHandler } from '@/api/client';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null);
  const initialized = ref(false);

  const isAuthenticated = computed(() => user.value != null);
  const isPlatformAdmin = computed(() => user.value != null);

  async function platformAdminLogin(
    identifier: string,
    password: string,
  ): Promise<LoginResponse> {
    const res = await apiPlatformAdminLogin(identifier, password);
    user.value = res.user;
    return res;
  }

  async function logout() {
    try {
      await apiLogout();
    } catch {
      // Clear local state even if the network request fails.
    } finally {
      user.value = null;
      try {
        localStorage.setItem(LOGOUT_SIGNAL_KEY, String(Date.now()));
        localStorage.removeItem(LOGOUT_SIGNAL_KEY);
      } catch {
        // best effort only
      }
    }
  }

  function handleUnauthorized() {
    user.value = null;
  }

  async function initializeAuth() {
    if (initialized.value) return;
    initialized.value = true;
    try {
      user.value = await fetchCurrentUser();
    } catch {
      user.value = null;
    }
  }

  setUnauthorizedHandler(handleUnauthorized);

  return {
    user,
    isAuthenticated,
    isPlatformAdmin,
    platformAdminLogin,
    logout,
    handleUnauthorized,
    initializeAuth,
  };
});
